
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Trash2, AlignLeft, AlignCenter, AlignRight, MoreHorizontal } from 'lucide-react';
import React from 'react';

interface ImageDropdownMenuProps {
  alignment: 'left' | 'center' | 'right';
  onAlignmentChange: (alignment: 'left' | 'center' | 'right') => void;
  width: number;
  onWidthChange: (width: number[]) => void;
  onWidthCommit: (width: number[]) => void;
  onDelete: () => void;
  caption: string;
  onCaptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSetCaption: () => void;
}

export default function ImageDropdownMenu({
  alignment,
  onAlignmentChange,
  width,
  onWidthChange,
  onWidthCommit,
  onDelete,
  caption,
  onCaptionChange,
  onSetCaption,
}: ImageDropdownMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="secondary"
          className="h-8 w-8 p-0 bg-black/70 hover:bg-black/90 rounded-full"
          type="button"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 z-50 bg-gray-900 border border-white/10">
        <div className="p-2">
          <p className="text-xs text-center mb-1">Alignment</p>
          <div className="flex justify-center gap-2 mb-2">
            <Button
              size="sm"
              variant={alignment === 'left' ? 'default' : 'secondary'}
              onClick={() => onAlignmentChange('left')}
              className="h-8 w-8 p-0"
              type="button"
            >
              <AlignLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant={alignment === 'center' ? 'default' : 'secondary'}
              onClick={() => onAlignmentChange('center')}
              className="h-8 w-8 p-0"
              type="button"
            >
              <AlignCenter className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant={alignment === 'right' ? 'default' : 'secondary'}
              onClick={() => onAlignmentChange('right')}
              className="h-8 w-8 p-0"
              type="button"
            >
              <AlignRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DropdownMenuSeparator className="bg-white/10" />

        <div className="p-2">
          <p className="text-xs text-center mb-1">Size: {width}%</p>
          <Slider
            value={[width]}
            min={20}
            max={100}
            step={5}
            onValueChange={onWidthChange}
            onValueCommit={onWidthCommit}
            className="my-2"
          />
        </div>

        <DropdownMenuSeparator className="bg-white/10" />

        <div className="p-2">
          <p className="text-xs text-center mb-1">Caption</p>
          <div className="flex gap-2">
            <input
              value={caption}
              onChange={onCaptionChange}
              placeholder="Add caption..."
              className="h-8 text-sm bg-black/30 border-white/20 rounded px-2 text-white"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={onSetCaption}
              className="h-8 whitespace-nowrap"
              type="button"
            >
              Set
            </Button>
          </div>
        </div>

        <DropdownMenuSeparator className="bg-white/10" />

        <DropdownMenuItem
          className="text-red-400 focus:text-red-400 cursor-pointer"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4 mr-2" /> Delete Image
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
